import axiosInstance from '../../services/axiosInstance';
import { BillingPayment } from './types/payment';

export const getPayments = async (skip = 0, limit = 10) => {
    try {
        const response = await axiosInstance.get('/service-payments', {
            params: {
                skip,
                limit
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getAllPayments = async () => {
    try {
        const allPayments: any[] = [];
        let skip = 0;
        const limit = 500;
        let hasMore = true;

        while (hasMore) {
            const response = await getPayments(skip, limit);

            if (response.length === 0) {
                hasMore = false;
            } else {
                allPayments.push(...response);
                skip += limit;
            }
        }

        return allPayments;
    } catch (error) {
        throw error;
    }
};

export const getServicePayments = async (serviceName: string) => {
    try {
        const response = await axiosInstance.get('/service-payments', {
            params: {
                service_name: serviceName,
                skip: 0,
                limit: 100
            }
        });
        return response.data;
    } catch (error) {
        throw error;
    }
};

export interface GatewayStatsSummary {
    mpamba: number;
    airtel_money: number;
    paypal: number;
    stripe: number;
}

export interface PaymentStats {
    payments: BillingPayment[];
    recentPayments: BillingPayment[];
    completedPayments: BillingPayment[];
    failedPayments: BillingPayment[];
    pendingPayments: BillingPayment[];
    gatewayStats: GatewayStatsSummary;
}

export const calculatePaymentStats = async (): Promise<PaymentStats> => {
    try {
        const payments = await getAllPayments();

        const stats = payments.reduce(
            (acc, payment) => {
                const now = new Date();
                const currentMonth = now.getMonth();
                const currentYear = now.getFullYear();

                acc.payments.push(payment);

                // Recent = current month
                const createdDate = new Date(payment.created_at);
                if (
                    createdDate.getMonth() === currentMonth &&
                    createdDate.getFullYear() === currentYear
                ) {
                    acc.recentPayments.push(payment);
                }

                // Status buckets
                if (payment.status === "COMPLETED") {
                    acc.completedPayments.push(payment);
                } else if (payment.status === "FAILED") {
                    acc.failedPayments.push(payment);
                } else if (payment.status === "PENDING") {
                    acc.pendingPayments.push(payment);
                }

                // Gateway counters
                if (payment.payment_gateway === "PawaPay") {
                    if (payment.mobile_operator === "TNM_MWI") {
                        acc.gatewayCounts.mpamba++;
                    } else if (payment.mobile_operator === "AIRTEL_MWI") {
                        acc.gatewayCounts.airtel_money++;
                    }
                } else if (payment.payment_gateway === "PayPal") {
                    acc.gatewayCounts.paypal++;
                } else if (payment.payment_gateway === "card") {
                    acc.gatewayCounts.stripe++;
                }

                return acc;
            },
            {
                payments: [] as BillingPayment[],
                recentPayments: [] as BillingPayment[],
                completedPayments: [] as BillingPayment[],
                failedPayments: [] as BillingPayment[],
                pendingPayments: [] as BillingPayment[],
                gatewayCounts: {
                    mpamba: 0,
                    airtel_money: 0,
                    paypal: 0,
                    stripe: 0
                }
            }
        );

        // Calculate percentages
        const totalPayments = stats.payments.length;
        // src/features/Payments/services.ts
        const gatewayStats: GatewayStatsSummary = {
            mpamba: totalPayments
                ? parseFloat(((stats.gatewayCounts.mpamba / totalPayments) * 100).toFixed(2))
                : 0,
            airtel_money: totalPayments
                ? parseFloat(((stats.gatewayCounts.airtel_money / totalPayments) * 100).toFixed(2))
                : 0,
            paypal: totalPayments
                ? parseFloat(((stats.gatewayCounts.paypal / totalPayments) * 100).toFixed(2))
                : 0,
            stripe: totalPayments
                ? parseFloat(((stats.gatewayCounts.stripe / totalPayments) * 100).toFixed(2))
                : 0
        };

        return {
            payments: stats.payments,
            recentPayments: stats.recentPayments,
            completedPayments: stats.completedPayments,
            failedPayments: stats.failedPayments,
            pendingPayments: stats.pendingPayments,
            gatewayStats
        };
    } catch (error) {
        throw error;
    }
};



